import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { 
  Leaf, ArrowLeft, TrendingUp, TrendingDown, Minus, 
  Loader2, Calendar, ChevronDown, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssessmentComparisonPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  
  const [comparison, setComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAssessments, setSelectedAssessments] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/compare');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchComparison = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        const ids = selectedAssessments.length > 0 ? `?assessment_ids=${selectedAssessments.join(',')}` : '';
        const res = await axios.get(`${API}/assessments/compare${ids}`, { headers: getAuthHeader() });
        setComparison(res.data);
      } catch (error) {
        console.error('Error fetching comparison:', error);
        toast.error('Failed to load comparison');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchComparison();
  }, [user, getAuthHeader, selectedAssessments]);

  const getTrendIcon = (trend) => {
    if (trend === 'improved') return <TrendingUp className="w-5 h-5 text-green-500" />;
    if (trend === 'declined') return <TrendingDown className="w-5 h-5 text-red-500" />;
    return <Minus className="w-5 h-5 text-gray-400" />;
  };

  const getChangeColor = (change) => {
    if (change > 5) return 'text-green-600';
    if (change < -5) return 'text-red-600';
    return 'text-gray-600';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-[#1A1A1A] flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Assessment Comparison
          </h1>
          <p className="text-muted-foreground mt-1">
            Track your health progress over time
          </p>
        </div>

        {!comparison || !comparison.comparison || !comparison.comparison.period ? (
          <div className="bg-white rounded-2xl border border-border p-8 text-center">
            <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Need more assessments</h2>
            <p className="text-muted-foreground mb-6">
              Complete at least 2 health assessments to compare your progress over time.
            </p>
            <Link to="/quiz">
              <Button className="btn-primary">Take Assessment</Button>
            </Link>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Period Summary */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-lg">Comparison Period</h2>
                <span className="text-sm text-muted-foreground">
                  {comparison.comparison?.total_assessments || 0} assessments
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span>{formatDate(comparison.comparison.period?.start)}</span>
                </div>
                <span className="text-muted-foreground">to</span>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span>{formatDate(comparison.comparison.period?.end)}</span>
                </div>
              </div>
            </div>

            {/* Score Changes */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="font-semibold text-lg mb-6">Health Score Changes</h2>
              <div className="grid gap-4">
                {Object.entries(comparison.comparison.score_changes || {})
                  .sort((a, b) => Math.abs(b[1].change) - Math.abs(a[1].change))
                  .map(([category, data]) => (
                    <div 
                      key={category}
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        {getTrendIcon(data.trend)}
                        <div>
                          <p className="font-medium capitalize">
                            {category.replace(/_/g, ' ')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {data.oldest}% → {data.latest}%
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${getChangeColor(data.change)}`}>
                          {data.change > 0 ? '+' : ''}{data.change}%
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {data.trend}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Individual Assessments */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="font-semibold text-lg mb-4">Assessment History</h2>
              <div className="space-y-3">
                {(comparison.assessments || []).map((assessment, index) => (
                  <Link
                    key={assessment.id}
                    to={`/results/${assessment.id}`}
                    className="block p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          Assessment #{comparison.assessments.length - index}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(assessment.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          {assessment.recommended_supplements?.length || 0} supplements
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {assessment.medications?.length || 0} medications
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Insights */}
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20 p-6">
              <h2 className="font-semibold text-lg mb-4">Key Insights</h2>
              <ul className="space-y-3">
                {Object.entries(comparison.comparison.score_changes || {})
                  .filter(([_, data]) => data.trend === 'improved')
                  .slice(0, 3)
                  .map(([category, data]) => (
                    <li key={category} className="flex items-start gap-2">
                      <TrendingUp className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        Your <strong className="capitalize">{category.replace(/_/g, ' ')}</strong> score 
                        improved by <strong className="text-green-600">{data.change}%</strong>
                      </span>
                    </li>
                  ))}
                {Object.entries(comparison.comparison.score_changes || {})
                  .filter(([_, data]) => data.trend === 'declined')
                  .slice(0, 2)
                  .map(([category, data]) => (
                    <li key={category} className="flex items-start gap-2">
                      <TrendingDown className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        Consider focusing on <strong className="capitalize">{category.replace(/_/g, ' ')}</strong> - 
                        score decreased by <strong className="text-amber-600">{Math.abs(data.change)}%</strong>
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
